import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  ClipboardList,
  Settings,
  PersonStanding,
  Dumbbell,
  Calendar,
  MessageSquare,
  TrendingUp,
  Target,
  Bot,
  CreditCard,
  UserPlus,
  Filter,
  MonitorUp,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdmin } from '@/contexts/AdminContext';
import { useSettings } from '@/components/settings/lib/settings-context';
import { useCanAccess } from '@/components/authnz/AbilityContext';
import { UserRole } from '@/components/authnz/types/rbac';
import { EXPERIMENTAL_FEATURES } from '@/config/experimental-features';
import { useShell } from '@/contexts/shell-context';
import { useAccess } from '@/lib/access/client';

interface SideNavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  experimental?: boolean;
}

const defaultItems: SideNavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <Home className="h-5 w-5" /> },
  { label: 'Clients', to: '/clients', icon: <Users className="h-5 w-5" /> },
  { label: 'Posture Analysis', to: '/posture-analysis', icon: <PersonStanding className="h-5 w-5" /> },
  { label: 'SOP Assessment', to: '/sop-assessment', icon: <ClipboardList className="h-5 w-5" /> },
  { label: 'Remote Assessment', to: '/remote-assessment', icon: <MonitorUp className="h-5 w-5" />, experimental: true },
  { label: 'Drill Library', to: '/drills', icon: <Dumbbell className="h-5 w-5" /> },
  { label: 'Drill Planner', to: '/drill-planner', icon: <Calendar className="h-5 w-5" />, experimental: true },
  { label: 'Ask Harry', to: '/chat', icon: <MessageSquare className="h-5 w-5" /> },
  { label: 'Progression Model', to: '/progression-model', icon: <TrendingUp className="h-5 w-5" /> },
  { label: 'Coaching', to: '/coaching', icon: <Target className="h-5 w-5" /> },
  { label: 'Settings', to: '/settings', icon: <Settings className="h-5 w-5" /> },
  { label: 'Help', to: '/help', icon: <HelpCircle className="h-5 w-5" /> },
];

export interface SideNavProps {
  items?: SideNavItem[];
}

function NavItemLink({
  item,
  showLabel,
  onNavigate,
}: {
  item: SideNavItem;
  showLabel: boolean;
  onNavigate: () => void;
}) {
  return (
    <li>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        title={!showLabel ? item.label : undefined}
        aria-label={item.label}
        className={({ isActive }) =>
          cn(
            'flex items-center rounded-md px-3 py-2.5 transition-colors',
            showLabel ? 'px-4' : 'justify-center',
            isActive
              ? 'bg-primary/10 font-medium text-primary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )
        }
      >
        <span className="h-5 w-5 flex-shrink-0">{item.icon}</span>
        {showLabel && (
          <span className="ml-3 flex items-center gap-2 whitespace-nowrap text-sm">
            {item.label}
            {(() => {
              const badgeType = EXPERIMENTAL_FEATURES.getBadgeType(item.to, item.experimental);
              if (badgeType === 'alpha') {
                return (
                  <span className="rounded border border-primary/30 bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                    ALPHA
                  </span>
                );
              }
              if (badgeType === 'beta') {
                return (
                  <span className="rounded border border-warning/30 bg-warning px-1.5 py-0.5 text-[10px] font-semibold text-warning-text">
                    BETA
                  </span>
                );
              }
              return null;
            })()}
          </span>
        )}
      </NavLink>
    </li>
  );
}

/**
 * Left navigation: icon rail on desktop (expand via header menu), slide-in drawer on mobile.
 */
export const SideNav: React.FC<SideNavProps> = ({ items = defaultItems }) => {
  const { navExpanded, navOpen, isMobile, closeNav } = useShell();
  const { isAdminMode, isAuthorizedAdmin } = useAdmin();
  const { settings } = useSettings();
  const { decision } = useAccess();
  const canReadProspects = useCanAccess('read', 'ClientProspect');
  const canReadFunnel = useCanAccess('read', 'FunnelAnalytics');
  const canCreateClient = useCanAccess('create', 'Client');
  const isSales = decision.roles.includes(UserRole.SALES);
  const canAccessDashboard = useCanAccess('access', 'Dashboard');
  const canCreateSubscription = useCanAccess('create', 'Subscription');

  const showLabels = isMobile || navExpanded;

  const navItems = useMemo(() => {
    const filtered = items
      .filter((item) => {
        if (item.to === '/drill-planner' && !settings.experimentalFeaturesEnabled) return false;
        // Remote Assessment shares a desktop call window. Phones still need the entry so they
        // can open the desktop-only explanation. The bottom bar does not list it.
        if (item.to === '/remote-assessment' && !settings.experimentalFeaturesEnabled) return false;
        return true;
      })
      .filter((item) => item.to !== '/agent')
      .filter((item) => canAccessDashboard || item.to === '/settings' || item.to === '/help');

    if (settings.experimentalFeaturesEnabled) {
      const hasAgent = filtered.some((item) => item.to === '/agent');
      if (!hasAgent) {
        const agentItem: SideNavItem = {
          label: 'Agent hArry',
          to: '/agent',
          icon: <Bot className="h-5 w-5" />,
          experimental: true,
        };
        const chatIndex = filtered.findIndex((item) => item.to === '/chat');
        if (chatIndex >= 0) filtered.splice(chatIndex + 1, 0, agentItem);
        else filtered.push(agentItem);
      }
    }
    return filtered;
  }, [items, settings.experimentalFeaturesEnabled, canAccessDashboard]);

  const salesMenuItems: SideNavItem[] = [
    ...(canReadProspects ? [{ label: 'Prospects', to: '/sales/prospects', icon: <UserPlus className="h-5 w-5" /> }] : []),
    ...(canReadFunnel ? [{ label: 'Funnel', to: '/sales/funnel', icon: <Filter className="h-5 w-5" /> }] : []),
    ...(isSales && canCreateClient
      ? [{ label: 'New Client', to: '/clients/new', icon: <Users className="h-5 w-5" /> }]
      : []),
  ];

  const adminMenuItems: SideNavItem[] = [
    { label: 'User Management', to: '/admin/users', icon: <Users className="h-5 w-5" /> },
    { label: 'Statistics', to: '/admin/statistics', icon: <TrendingUp className="h-5 w-5" /> },
  ];

  const subscriptionMenuItems: SideNavItem[] = canCreateSubscription
    ? [{ label: 'Subscriptions', to: '/subscriptions', icon: <CreditCard className="h-5 w-5" /> }]
    : [];

  const handleNavigate = () => {
    if (isMobile) closeNav();
  };

  return (
    <aside
      id="app-sidebar"
      aria-label="Main navigation"
      className={cn(
        'sidebar flex flex-col border-r border-border bg-card transition-all duration-300 ease-out',
        isMobile
          ? cn(
              'fixed inset-y-0 left-0 z-50 w-56 shadow-xl',
              navOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
            )
          : cn('hidden lg:flex shrink-0', navExpanded ? 'w-56' : 'w-16')
      )}
    >
      <nav className="flex-1 overflow-y-auto pt-2">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => (
            <NavItemLink key={item.to} item={item} showLabel={showLabels} onNavigate={handleNavigate} />
          ))}
          {salesMenuItems.length > 0 && (
            <>
              <li className="mt-4 border-t border-border pt-4">
                {showLabels && (
                  <div className="px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sales</span>
                  </div>
                )}
              </li>
              {salesMenuItems.map((item) => (
                <NavItemLink key={item.to} item={item} showLabel={showLabels} onNavigate={handleNavigate} />
              ))}
            </>
          )}
          {subscriptionMenuItems.map((item) => (
            <NavItemLink key={item.to} item={item} showLabel={showLabels} onNavigate={handleNavigate} />
          ))}
          {isAdminMode && isAuthorizedAdmin && (
            <>
              <li className="mt-4 border-t border-border pt-4">
                {showLabels && (
                  <div className="px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin</span>
                  </div>
                )}
              </li>
              {adminMenuItems.map((item) => (
                <NavItemLink key={item.to} item={item} showLabel={showLabels} onNavigate={handleNavigate} />
              ))}
            </>
          )}
        </ul>
      </nav>
    </aside>
  );
};

export default SideNav;
